import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { crewLabel, usePermissions, canReschedule } from "@/lib/permissions";
import { PhotoDeleteDialog } from "@/components/PhotoDeleteDialog";
import {
  CheckCircle,
  MapPin,
  NavigationArrow,
  Phone,
  Camera,
  X,
  PaperPlaneTilt,
  Clock,
  CaretRight,
  User,
  CalendarBlank,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { areaColor } from "@/lib/areaColor";
import { fmtTime12 } from "@/lib/scheduleWindows";
import { ConfirmTextDialog } from "@/components/ConfirmTextDialog";
import { stopKey } from "@/lib/routeStops";
import { MobileTeamNotes } from "@/components/MobileTeamNotes";
import { TemplateChecklistSheet } from "@/components/TemplateChecklistSheet";
import { InstallSignoffSheet } from "@/components/InstallSignoffSheet";
import { RescheduleSheet } from "@/components/RescheduleSheet";

export const todayIso = () => new Date().toISOString().slice(0, 10);
const NEXT = { Pending: "On site", "On site": "Done", Done: "Pending" };

export const STATUS_STYLE = {
  Pending: "border-zinc-700 bg-[#0F172A] text-zinc-300",
  "On site": "border-yellow-500 bg-yellow-500/15 text-yellow-300",
  Done: "border-emerald-500 bg-emerald-500/15 text-emerald-300",
};
export const KIND_STYLE = {
  Template: "border-purple-500/30 text-purple-300",
  Fabrication: "border-yellow-500/30 text-yellow-300",
  Install: "border-blue-500/30 text-blue-300",
  Service: "border-red-500/30 text-red-300",
};
export const KIND_ORDER = { Template: 0, Fabrication: 1, Install: 2, Service: 3 };
export const DOT_HEX = {
  Template: "#c084fc",
  Fabrication: "#facc15",
  Install: "#60a5fa",
  Service: "#f87171",
};

export const directionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

export const phoneKey = (p) => (p || "").replace(/\D/g, "").slice(-10);
// Verb for the areas a stop covers, e.g. "Templating Kitchen · Master Bath".
export const areaVerb = (s) => {
  const k = s.entity === "service" ? "Service" : s.kind;
  if (k === "Template") return "Templating";
  if (k === "Install") return "Installing";
  if (k === "Fabrication") return "Fabricating";
  return "Areas";
};

const photoUrl = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/photos/${path}?auth=${localStorage.getItem("sw_token")}`;
const livePhotos = (job) => (job?.photos || []).filter((p) => !p.is_deleted);

const fmtClock = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export async function cycleStop(stop, apply) {
  const current = stop.stop_status?.status || "Pending";
  const next = NEXT[current];
  const url =
    stop.entity === "service"
      ? `/service-calls/${stop.job_id}/status`
      : stop.entity === "project"
        ? `/projects/${stop.project_id}/tasks/${stop.task_id || stop.stop_id}/status`
        : `/jobs/${stop.job_id}/stops/${stop.kind}${
            stop.stop_id ? `?stop_id=${stop.stop_id}` : ""
          }`;
  apply(next);
  try {
    await api.patch(url, { status: next });
    toast.success(`${stop.code} ${stop.kind} · ${next}`);
  } catch (err) {
    apply(current);
    const detail = err.response?.data?.detail;
    if (detail?.code === "checklist_incomplete") {
      toast.error(`Template checklist: ${detail.missing.length} item(s) still unticked`);
      return;
    }
    toast.error(
      formatApiErrorDetail(detail) || "Could not update",
    );
  }
}

export const stopIsMine = (s, user) => {
  const crew = (s.crew || "").trim().toLowerCase();
  if (!crew) return false;
  const me = [user?.name, user?.email]
    .map((x) => (x || "").toLowerCase())
    .filter(Boolean);
  if (me.includes(crew)) return true;
  const role = (user?.role || "").toLowerCase();
  return !!role && crew === `group:${role}`;
};

export function MobileStopCard({ s, onCycle, unread, onReschedule }) {
  const status = s.stop_status?.status || "Pending";
  const kid = s.stop_id || s.kind;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [texting, setTexting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [late, setLate] = useState(0);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const lateMessage = (mins) => {
    const base = s.route_eta ? new Date(new Date(s.route_eta).getTime() + mins * 60000) : null;
    const fmt = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const win = base
      ? `now expected between ${fmt(new Date(base.getTime() - 15 * 60000))} and ${fmt(new Date(base.getTime() + 30 * 60000))}`
      : `running about ${mins} minutes behind`;
    return `Hi${s.customer_name ? " " + s.customer_name.split(" ")[0] : ""}, this is Brioni Granite — our crew is running about ${mins} min late for your ${s.kind.toLowerCase()} and is ${win}. Sorry for the delay, see you soon!`;
  };

  const doSendLate = async (message) => {
    setTexting(true);
    try {
      await api.post("/sms/send", {
        to: s.customer_phone,
        body: message,
        entity: isProject ? "project" : s.entity === "service" ? "service" : "job",
        entity_id: entityId,
        tag: "running_late",
        late_minutes: late,
      });
      if (s.route_eta) {
        await api.post("/route/shift-eta", {
          date: s.date, crew: s.crew || "", stop: stopKey(s), minutes: late,
        }).catch(() => {});
      }
      toast.success(`Running-late text sent (+${late} min)`);
      setLate(0);
      onCycle?.("refresh");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send text");
    }
    setTexting(false);
  };
  const [photos, setPhotos] = useState([]);
  const [capId, setCapId] = useState("");
  const [delId, setDelId] = useState("");
  const { can } = usePermissions();
  const { role } = usePermissions();
  const canDelete = can("delete:photos");
  const [capText, setCapText] = useState("");
  const isProject = s.entity === "project";
  const isService = s.entity === "service";
  const isFab = s.kind === "Fabrication";
  const taskId = s.task_id || s.stop_id;
  const entityId = isProject ? s.project_id : s.job_id;
  const canPhoto = !!entityId;
  const detailUrl = isProject
    ? `/projects/${s.project_id}`
    : isService
      ? `/mobile/service/${s.job_id}`
      : `/mobile/jobs/${s.job_id}?kind=${encodeURIComponent(s.kind)}`;
  const photoBase = isProject
    ? `/projects/${s.project_id}/tasks/${taskId}/photos`
    : isService
      ? `/service-calls/${s.job_id}/photos`
      : `/jobs/${s.job_id}/photos`;

  useEffect(() => {
    if (!canPhoto) return undefined;
    if (isProject) {
      setPhotos((s.photos || []).filter((p) => !p.is_deleted));
      return undefined;
    }
    let alive = true;
    api
      .get(isService ? `/service-calls/${s.job_id}` : `/jobs/${s.job_id}`)
      .then(({ data }) => {
        if (alive) setPhotos(livePhotos(data));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [canPhoto, isProject, isService, s.job_id, s.photos]);

  const [checklistOpen, setChecklistOpen] = useState(false);
  const [signoffOpen, setSignoffOpen] = useState(false);
  const handleCycle = () => {
    const current = s.stop_status?.status || "Pending";
    if (s.kind === "Template" && !isService && !isProject && !s.stop_id && current === "On site") {
      setChecklistOpen(true);
      return;
    }
    if (s.kind === "Install" && !isService && !isProject && !s.stop_id && current === "On site") {
      setSignoffOpen(true);
      return;
    }
    onCycle();
  };

  const omwMessage = () =>
    `Hi${
      s.customer_name ? " " + s.customer_name.split(" ")[0] : ""
    }, this is Brioni Granite — our crew is on the way now for your ${s.kind.toLowerCase()}${
      s.window && s.window !== "All day" ? ` (arrival window ${s.window})` : ""
    }. See you shortly!`;

  const doSendOnMyWay = async (message) => {
    setTexting(true);
    try {
      await api.post("/sms/send", {
        to: s.customer_phone,
        body: message,
        entity: isProject ? "project" : s.entity === "service" ? "service" : "job",
        entity_id: entityId,
      });
      toast.success("On-the-way text sent");
      setConfirm(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send text",
      );
    }
    setTexting(false);
  };

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(photoBase, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos(livePhotos(data));
      toast.success(`Photo added to ${s.code}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
    setUploading(false);
  };

  const removePhoto = async (pid) => {
    try {
      const { data } = await api.delete(`${photoBase}/${pid}`);
      setPhotos(livePhotos(data));
      if (capId === pid) setCapId("");
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const openCaption = (p) => {
    setCapId(p.id);
    setCapText(p.caption || "");
  };

  const saveCaption = async () => {
    const pid = capId;
    if (!pid) return;
    try {
      const { data } = await api.patch(`${photoBase}/${pid}`, {
        caption: capText,
      });
      setPhotos(livePhotos(data));
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    }
    setCapId("");
  };

  const iconBtn =
    "flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-700 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 disabled:opacity-50";

  return (
    <div
      data-testid={`mobile-stop-${s.job_id}-${kid}`}
      className={`rounded-2xl border p-4 shadow-lg shadow-black/40 ring-1 ring-white/5 transition-colors duration-150 ${
        status === "Done"
          ? "border-emerald-500/40 bg-[#111C32]/70"
          : "border-slate-700 bg-[#111C32]"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
          style={{
            borderColor: `${s.color || DOT_HEX[s.kind] || "#71717a"}55`,
            color: s.color || DOT_HEX[s.kind] || "#d4d4d8",
          }}
        >
          {s.kind} · {[s.time ? fmtTime12(s.time) : "", s.window && s.window !== "All day" ? s.window : ""].filter(Boolean).join(" · ") || "All day"}
        </span>
        <Link
          to={detailUrl}
          className="font-mono-data text-xs text-yellow-500 hover:underline"
        >
          {s.code}
        </Link>
        {unread && (
          <span
            data-testid={`mobile-stop-unread-${s.job_id}-${kid}`}
            title="Customer replied"
            className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold text-red-300"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Reply
          </span>
        )}
        {s.label && (
          <span
            data-testid={`mobile-stop-label-${s.job_id}-${kid}`}
            className="rounded-full border border-[#c39e6d]/30 bg-[#c39e6d]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#c39e6d]"
          >
            {s.label}
          </span>
        )}
        {s.require_photo && !isFab && (
          <span
            data-testid={`mobile-stop-photo-flag-${s.job_id}-${kid}`}
            className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300"
          >
            <Camera size={11} weight="bold" /> Photo
          </span>
        )}
        {canPhoto && !isFab && (
          <span
            data-testid={`mobile-stop-photocount-${s.job_id}-${kid}`}
            title={
              photos.length
                ? `${photos.length} photo${photos.length > 1 ? "s" : ""}`
                : "No photos yet"
            }
            className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${
              photos.length > 0
                ? "border-emerald-500/30 text-emerald-300"
                : "border-amber-500/40 text-amber-300"
            }`}
          >
            <Camera size={11} weight="bold" />
            {photos.length}
          </span>
        )}
        <span className="ml-auto truncate text-xs text-zinc-500">
          {crewLabel(s.crew)}
        </span>
      </div>

      <Link
        to={detailUrl}
        data-testid={`mobile-stop-open-${s.job_id}-${kid}`}
        className="mt-2 block text-base font-bold leading-tight text-zinc-100 transition-colors duration-150 hover:text-[#c39e6d]"
      >
        {s.project_name}
      </Link>
      <div className="text-sm text-zinc-300">{s.customer_company || s.customer_name || "—"}</div>
      {s.areas?.length > 0 && (
        <div
          data-testid={`mobile-stop-areas-${s.job_id}-${kid}`}
          className="mt-1.5 flex flex-wrap items-center gap-1.5"
        >
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            {areaVerb(s)}
          </span>
          {s.areas.map((a) => (
            <span
              key={a}
              className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-[10px] font-semibold text-zinc-200"
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: areaColor(a) }}
              />
              {a}
            </span>
          ))}
        </div>
      )}
      <MobileTeamNotes projectId={s.project_id} testid={`mobile-stop-${s.job_id}-${kid}`} />
      {checklistOpen && (
        <TemplateChecklistSheet
          jobId={s.job_id}
          code={s.code}
          onClose={() => setChecklistOpen(false)}
          onComplete={async () => onCycle()}
        />
      )}
      {signoffOpen && (
        <InstallSignoffSheet
          jobId={s.job_id}
          code={s.code}
          customerName={s.customer_name}
          photoCount={photos.length}
          onClose={() => setSignoffOpen(false)}
          onSigned={async () => onCycle()}
        />
      )}
      {s.address && (
        <div className="mt-1 flex items-start gap-1.5 text-xs text-zinc-400">
          <MapPin
            size={13}
            weight="bold"
            className="mt-0.5 shrink-0 text-zinc-500"
          />
          <span className="min-w-0">{s.address}</span>
        </div>
      )}

      {(s.stop_status?.on_site_at || s.stop_status?.done_at) && (
        <div
          data-testid={`mobile-stop-times-${s.job_id}-${kid}`}
          className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]"
        >
          {s.stop_status?.on_site_at && (
            <span className="inline-flex items-center gap-1.5 text-yellow-300/80">
              <span className="h-1.5 w-1.5 rounded-full bg-yellow-400" />
              Arrived {fmtClock(s.stop_status.on_site_at)}
            </span>
          )}
          {s.stop_status?.done_at && (
            <span className="inline-flex items-center gap-1.5 text-emerald-300/80">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Finished {fmtClock(s.stop_status.done_at)}
            </span>
          )}
        </div>
      )}

      {canPhoto && status === "Done" && photos.length === 0 && !isFab && (
        <button
          type="button"
          data-testid={`mobile-stop-photo-reminder-${s.job_id}-${kid}`}
          onClick={() => fileRef.current?.click()}
          className="mt-3 flex w-full items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-left text-xs text-amber-300 transition-colors duration-150 hover:bg-amber-500/10"
        >
          <Camera size={14} weight="bold" className="shrink-0" />
          <span>Marked done — add a completion photo before you wrap up</span>
        </button>
      )}

      {photos.length > 0 && !isFab && (
        <div className="mt-3">
          <div
            className="flex gap-2 overflow-x-auto pb-1"
            data-testid={`mobile-stop-gallery-${s.job_id}-${kid}`}
          >
            {photos.map((p) => (
              <div key={p.id} className="w-16 shrink-0">
                <div className="relative">
                  <a
                    href={photoUrl(p.storage_path)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`mobile-stop-photo-open-${p.id}`}
                  >
                    <img
                      src={photoUrl(p.thumb_path || p.storage_path)}
                      alt={p.caption || "Job"}
                      className="h-16 w-16 rounded-lg border border-zinc-700 object-cover"
                    />
                  </a>
                  {canDelete && (
                  <button
                    type="button"
                    data-testid={`mobile-stop-photo-delete-${p.id}`}
                    onClick={() => setDelId(p.id)}
                    title="Delete photo"
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-zinc-700 bg-[#080C14] text-zinc-300 transition-colors duration-150 hover:text-red-400"
                  >
                    <X size={11} weight="bold" />
                  </button>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`mobile-stop-caption-${p.id}`}
                  onClick={() => openCaption(p)}
                  className={`mt-1 block w-16 truncate text-center text-[10px] transition-colors duration-150 ${
                    p.caption
                      ? "text-zinc-300 hover:text-[#c39e6d]"
                      : "text-zinc-600 hover:text-[#c39e6d]"
                  }`}
                >
                  {p.caption || "+ note"}
                </button>
              </div>
            ))}
          </div>
          {capId && (
            <div className="mt-2 flex items-center gap-2">
              <input
                data-testid="mobile-stop-caption-input"
                value={capText}
                onChange={(e) => setCapText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveCaption();
                }}
                placeholder="Add a note (e.g. seam at sink)"
                autoFocus
                className="h-10 min-w-0 flex-1 rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
              />
              <button
                type="button"
                data-testid="mobile-stop-caption-save"
                onClick={saveCaption}
                className="h-10 shrink-0 rounded-xl bg-[#c39e6d] px-4 text-[11px] font-bold uppercase tracking-widest text-slate-950 transition-colors duration-150 hover:bg-[#c39e6d]"
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2 border-t border-zinc-800 pt-3">
        {s.customer_phone && !isFab && (
          <button
            type="button"
            data-testid={`mobile-stop-omw-${s.job_id}-${kid}`}
            onClick={() => s.customer_phone && setConfirm(true)}
            disabled={texting}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#c39e6d]/50 bg-[#c39e6d]/10 text-[11px] font-bold uppercase tracking-widest text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d]/20 disabled:opacity-50"
          >
            <PaperPlaneTilt size={15} weight="bold" />
            {texting ? "Sending…" : "Text customer · On my way"}
          </button>
        )}
        {s.customer_phone && status !== "Done" && !isFab && (
          <div className="flex items-center gap-1.5" data-testid={`mobile-stop-late-${s.job_id}-${kid}`}>
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-300/80">
              <Clock size={12} weight="bold" /> Running late
            </span>
            {[15, 30, 45, 60].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setLate(m)}
                disabled={texting}
                data-testid={`mobile-stop-late-${m}-${s.job_id}-${kid}`}
                className="h-8 flex-1 rounded-lg border border-amber-500/30 bg-amber-500/5 text-[11px] font-bold text-amber-200 transition-colors duration-150 hover:bg-amber-500/15 disabled:opacity-50"
              >
                +{m}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          {s.customer_phone && (
            <a
              href={`tel:${s.customer_phone}`}
              data-testid={`mobile-stop-call-${s.job_id}-${kid}`}
              className={iconBtn}
              title="Call customer"
            >
              <Phone size={16} weight="bold" />
            </a>
          )}
          {s.address && !isFab && (
            <a
              href={directionsUrl(s.address)}
              target="_blank"
              rel="noreferrer"
              data-testid={`mobile-stop-directions-${s.job_id}-${kid}`}
              className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-zinc-700 px-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-zinc-800"
              title="Get directions"
            >
              <NavigationArrow size={16} weight="bold" />
              Directions
            </a>
          )}
          {canPhoto && !isFab && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                data-testid={`mobile-stop-photo-input-${s.job_id}-${kid}`}
                onChange={onPhoto}
              />
              <button
                type="button"
                data-testid={`mobile-stop-photo-${s.job_id}-${kid}`}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className={iconBtn}
                title="Add photo"
              >
                <Camera size={16} weight="bold" />
              </button>
            </>
          )}
          {canReschedule(role) &&
            !isProject &&
            !isService &&
            !s.stop_id &&
            ["Template", "Fabrication", "Install"].includes(s.kind) && (
              <button
                type="button"
                data-testid={`mobile-stop-reschedule-${s.job_id}-${kid}`}
                onClick={() => setRescheduleOpen(true)}
                className={iconBtn}
                title="Reschedule"
              >
                <CalendarBlank size={16} weight="bold" />
              </button>
            )}
          <button
            type="button"
            data-testid={`mobile-stop-toggle-${s.job_id}-${kid}`}
            onClick={handleCycle}
            className={`ml-auto inline-flex h-10 items-center rounded-xl border px-4 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              status === "Done"
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                : "border-[#c39e6d]/60 bg-[#c39e6d]/15 text-[#c39e6d] hover:bg-[#c39e6d]/25"
            }`}
          >
            {status === "Done" ? (
              <>
                <CheckCircle size={16} weight="bold" className="mr-1.5" />
                Done
              </>
            ) : (
              `Mark ${NEXT[status]}`
            )}
          </button>
        </div>
      </div>
      <PhotoDeleteDialog
        open={!!delId}
        onOpenChange={(o) => !o && setDelId("")}
        onConfirm={async () => {
          await removePhoto(delId);
          setDelId("");
        }}
        testId="mobile-stop-photo-delete-dialog"
      />
      <ConfirmTextDialog
        open={confirm}
        onOpenChange={setConfirm}
        to={s.customer_phone}
        firstName={(s.customer_name || "").split(" ")[0]}
        message={omwMessage()}
        title="On my way — review text"
        sending={texting}
        onSend={doSendOnMyWay}
      />
      <ConfirmTextDialog
        open={late > 0}
        onOpenChange={(o) => !o && setLate(0)}
        to={s.customer_phone}
        firstName={(s.customer_name || "").split(" ")[0]}
        message={late ? lateMessage(late) : ""}
        title={`Running ${late} min late — review text`}
        sending={texting}
        onSend={doSendLate}
      />
      <RescheduleSheet
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        jobId={s.job_id}
        kind={s.kind}
        current={{ date: s.date || "", window: s.window || "All day", time: s.time || "" }}
        onDone={() => onReschedule?.()}
      />
    </div>
  );
}


const stopDetailUrl = (s) =>
  s.entity === "project"
    ? `/projects/${s.project_id}`
    : s.entity === "service"
      ? `/mobile/service/${s.job_id}`
      : `/mobile/jobs/${s.job_id}`;

const stopTime = (s) =>
  s.time ? fmtTime12(s.time) : s.window && s.window !== "All day" ? s.window : "";

// The big "in progress / up next" card at the top of My Day.
export function StopHero({ s, onCycle, onReschedule }) {
  const { role } = usePermissions();
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const status = s.stop_status?.status || "Pending";
  const kind = s.entity === "service" ? "Service" : s.kind;
  const isFab = s.kind === "Fabrication";
  const time = stopTime(s) || "All day";
  const detailUrl = stopDetailUrl(s);
  const iconBtn =
    "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-zinc-700 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800";
  return (
    <div
      data-testid={`mobile-hero-${s.job_id}`}
      className="overflow-hidden rounded-3xl border border-[#c39e6d]/40 bg-gradient-to-b from-[#c39e6d]/10 to-zinc-900/50 p-4 shadow-[0_0_45px_-14px_rgba(99,102,241,0.55)]"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          className={`rounded-lg border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[status]}`}
        >
          {status === "On site" ? "In progress" : status}
        </span>
        <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          {kind}
        </span>
        <span className="flex items-center gap-1 text-[12px] font-bold text-zinc-200">
          <Clock size={13} weight="bold" className="text-zinc-500" />
          {time}
        </span>
        {s.crew && (
          <span className="ml-auto flex items-center gap-1 rounded-lg border border-[#c39e6d]/40 bg-[#c39e6d]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#c39e6d]">
            <User size={12} weight="bold" />
            {crewLabel(s.crew)}
          </span>
        )}
      </div>

      <Link
        to={detailUrl}
        data-testid={`mobile-hero-open-${s.job_id}`}
        className="mt-3 block text-2xl font-bold leading-tight text-zinc-50 transition-colors duration-150 hover:text-[#c39e6d]"
      >
        {s.project_name}
      </Link>

      {s.areas?.length > 0 && (
        <div
          data-testid={`mobile-hero-areas-${s.job_id}`}
          className="mt-2 flex flex-wrap items-center gap-1.5"
        >
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#c39e6d]/80">
            {areaVerb(s)}
          </span>
          {s.areas.map((a) => (
            <span
              key={a}
              className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/70 px-2 py-0.5 text-[11px] font-semibold text-zinc-100"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: areaColor(a) }}
              />
              {a}
            </span>
          ))}
        </div>
      )}

      {s.address && (
        <div className="mt-2.5 flex items-start gap-2.5 text-[15px] leading-snug text-zinc-300">
          <MapPin size={17} weight="bold" className="mt-0.5 shrink-0 text-zinc-500" />
          {s.address}
        </div>
      )}
      {(s.customer_company || s.customer_name || s.customer_phone) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[15px] text-zinc-300">
          <User size={17} weight="bold" className="shrink-0 text-zinc-500" />
          {(s.customer_company || s.customer_name) && (
            <span>{s.customer_company || s.customer_name}</span>
          )}
          {s.customer_phone && (
            <span className="font-mono-data text-zinc-400">{s.customer_phone}</span>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 border-t border-white/5 pt-4">
        <button
          type="button"
          data-testid={`mobile-hero-complete-${s.job_id}`}
          onClick={onCycle}
          className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border text-sm font-bold uppercase tracking-widest transition-colors duration-150 ${
            status === "Done"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
          }`}
        >
          <CheckCircle size={18} weight="bold" />
          {status === "Done" ? "Done" : status === "On site" ? "Complete" : "Start / Complete"}
        </button>
        {s.customer_phone && (
          <a
            href={`tel:${s.customer_phone}`}
            data-testid={`mobile-hero-call-${s.job_id}`}
            className={iconBtn}
            aria-label="Call"
          >
            <Phone size={18} weight="bold" />
          </a>
        )}
        {s.address && !isFab && (
          <a
            href={directionsUrl(s.address)}
            target="_blank"
            rel="noreferrer"
            data-testid={`mobile-hero-directions-${s.job_id}`}
            className={iconBtn}
            aria-label="Directions"
          >
            <MapPin size={18} weight="bold" />
          </a>
        )}
        {canReschedule(role) &&
          !isFab &&
          s.entity !== "service" &&
          s.entity !== "project" &&
          !s.stop_id &&
          ["Template", "Fabrication", "Install"].includes(s.kind) && (
            <button
              type="button"
              data-testid={`mobile-hero-reschedule-${s.job_id}`}
              onClick={() => setRescheduleOpen(true)}
              className={iconBtn}
              aria-label="Reschedule"
            >
              <CalendarBlank size={18} weight="bold" />
            </button>
          )}
      </div>
      <RescheduleSheet
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        jobId={s.job_id}
        kind={s.kind}
        current={{ date: s.date || "", window: s.window || "All day", time: s.time || "" }}
        onDone={() => onReschedule?.()}
      />
    </div>
  );
}

// Compact navigational row used in "Other stops" and the Map "stops in order" list.
export function StopTile({ s, index }) {
  const status = s.stop_status?.status || "Pending";
  const done = status === "Done";
  const onSite = status === "On site";
  const kind = s.entity === "service" ? "Service" : s.kind;
  const time = stopTime(s);
  const detailUrl = stopDetailUrl(s);
  return (
    <Link
      to={detailUrl}
      data-testid={`mobile-tile-${s.job_id}-${s.stop_id || s.kind}`}
      className="flex items-center gap-3 rounded-2xl border border-slate-700 bg-[#111C32] p-3 shadow-md shadow-black/30 ring-1 ring-white/5 transition-colors duration-150 hover:border-slate-600 hover:bg-[#15213a]"
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border text-sm font-bold ${
          done
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
            : onSite
              ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-300"
              : "border-[#c39e6d]/30 bg-[#c39e6d]/10 text-[#c39e6d]"
        }`}
      >
        {done ? <CheckCircle size={20} weight="bold" /> : index}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-bold text-zinc-100">
          {s.project_name}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          <span className="truncate">{s.label || kind}</span>
          {time && (
            <>
              <span className="text-zinc-700">·</span>
              <Clock size={11} weight="bold" />
              {time}
            </>
          )}
          {s.route_eta && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="text-[#c39e6d]" data-testid={`mobile-tile-eta-${s.job_id}-${s.stop_id || s.kind}`}>
                ETA {new Date(s.route_eta).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </span>
            </>
          )}
          {s.window && s.window !== "All day" && s.window !== time && (
            <>
              <span className="text-zinc-700">·</span>
              {s.window}
            </>
          )}
        </span>
        {s.areas?.length > 0 && (
          <span
            data-testid={`mobile-tile-areas-${s.job_id}-${s.stop_id || s.kind}`}
            className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] font-semibold text-zinc-400"
          >
            <span className="uppercase tracking-wider text-zinc-500">{areaVerb(s)}</span>
            {s.areas.map((a) => (
              <span key={a} className="inline-flex items-center gap-1">
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: areaColor(a) }}
                />
                {a}
              </span>
            ))}
          </span>
        )}
      </span>
      <CaretRight size={18} weight="bold" className="shrink-0 text-zinc-600" />
    </Link>
  );
}
